import * as React from "react";
import { api, type ApiOrg, type ApiUser, type WebhookEventSummary, type WebhookRegistration } from "../api.js";
import { Icon } from "../components/Icon.js";

type Props = {
  user: ApiUser;
  org: ApiOrg | null;
  instance: string;
};

// Reusable JSON viewer with a copy button. Uses the async Clipboard
// API when available (all evergreen browsers over HTTPS) and falls
// back to a hidden textarea + execCommand so it still works over
// plain HTTP in local dev.
function JsonBlock({ value }: { value: unknown }) {
  const [copied, setCopied] = React.useState(false);
  const text = React.useMemo(() => JSON.stringify(value, null, 2), [value]);
  const copy = React.useCallback(() => {
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {
        legacyCopy(text);
        done();
      });
    } else {
      legacyCopy(text);
      done();
    }
  }, [text]);
  return (
    <div style={{ position: "relative" }}>
      <button
        className="btn btn-ghost btn-sm"
        onClick={copy}
        style={{ position: "absolute", top: 6, right: 6, zIndex: 1 }}
        aria-label="Copy JSON"
      >
        {copied ? "Copied" : "Copy"}
      </button>
      <pre style={{
        background: "var(--ink-50)",
        padding: 12,
        paddingRight: 72,
        borderRadius: 6,
        overflow: "auto",
        maxHeight: 400,
        fontSize: 12,
        margin: 0,
      }}>{text}</pre>
    </div>
  );
}

function legacyCopy(text: string): void {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } catch { /* ignore */ }
  document.body.removeChild(ta);
}

function fmt(ms: number | null | undefined): string {
  if (!ms) return "—";
  const n = typeof ms === "number" && ms < 1e12 ? ms * 1000 : (ms as number);
  return new Date(n).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function WebhooksScreen({ user, org, instance }: Props) {
  const [regs, setRegs] = React.useState<WebhookRegistration[] | null>(null);
  const [events, setEvents] = React.useState<WebhookEventSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [kindInput, setKindInput] = React.useState("event.pack.*");
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [triggered, setTriggered] = React.useState<Array<{ id?: string; kind: string; object?: string; created?: number; data?: unknown }> | null>(null);
  const [triggeredExpanded, setTriggeredExpanded] = React.useState<Record<string, boolean>>({});

  const reload = React.useCallback(async () => {
    const [regsRes, evtRes] = await Promise.all([api.webhooks.list(), api.webhooks.events(100)]);
    setRegs(regsRes.registrations);
    setEvents(evtRes.events);
  }, []);

  React.useEffect(() => {
    let alive = true;
    reload().catch((e) => { if (alive) setError(String(e?.message ?? e)); });
    return () => { alive = false; };
  }, [reload]);

  // Manual "Refresh events" is enough during rollout; a real-time
  // stream isn't worth the complexity while we're still inspecting the
  // payload shape by hand.
  const refreshEvents = React.useCallback(async () => {
    try {
      const evtRes = await api.webhooks.events(100);
      setEvents(evtRes.events);
    } catch (e) { setError(String((e as Error).message ?? e)); }
  }, []);

  const fetchTriggered = React.useCallback(async (kinds?: string[]) => {
    setError(null);
    try {
      const res = await api.webhooks.triggered({ ...(kinds ? { kinds } : {}), limit: 50 });
      setTriggered(res.events);
    } catch (e) { setError(String((e as Error).message ?? e)); }
  }, []);

  async function handleCreate() {
    setBusy(true); setError(null);
    try {
      await api.webhooks.create({ matchingKind: kindInput.trim() });
      await reload();
    } catch (e) { setError(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  }

  async function handleRemove(id: string) {
    if (!confirm("Delete this webhook registration? TidyHQ will stop sending events to this server.")) return;
    setBusy(true); setError(null);
    try { await api.webhooks.remove(id); await reload(); }
    catch (e) { setError(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  }

  async function handleToggle(reg: WebhookRegistration) {
    setBusy(true); setError(null);
    try {
      await api.webhooks.setActive(reg.id, reg.status !== "active");
      await reload();
    } catch (e) { setError(String((e as Error).message ?? e)); }
    finally { setBusy(false); }
  }

  if (error === "unauthenticated") { window.location.href = "/auth/login"; return null; }

  const userInitials = (() => {
    const name = user.displayName ?? user.email ?? "?";
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
    if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
    return "?";
  })();
  const orgInitial = (org?.name ?? "?")[0]!.toUpperCase();

  return (
    <div className="dash-root">
      <header className="topbar">
        <div className="tb-l">
          <div className="brand-mark"><span className="dot" />Tidy<b>View</b></div>
          <div className="org-pill" title={org?.name ?? ""}>
            <div className="avatar" style={{ background: "#1a8fbf", width: 22, height: 22, fontSize: 10 }}>{orgInitial}</div>
            <span>{org?.name ?? "—"}</span>
          </div>
        </div>
        <div className="tb-r">
          <a href="#/" className="btn btn-secondary btn-sm">
            <Icon name="chevLeft" size={13} /> Back to dashboard
          </a>
          <div className="avatar" style={{ background: "#1a8fbf" }} title={user.displayName ?? ""}>{userInitials}</div>
        </div>
      </header>

      <main style={{ padding: "24px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22, letterSpacing: "-0.01em", color: "var(--ink-900)" }}>Webhooks</h1>
          <p className="muted" style={{ margin: "6px 0 0", fontSize: 13.5, maxWidth: 720 }}>
            Register a TidyHQ webhook so this server receives event
            deliveries directly. We're using this to capture the shape
            of <code>event.pack.activated</code>, which is expected to
            carry attendee contact IDs — once we've locked the shape
            down it'll replace the manual CSV overrides.
          </p>
        </div>

        {error && (
          <div className="card" style={{ padding: 12, marginBottom: 12, color: "var(--red-500)" }}>
            <b>Error:</b> {error}
          </div>
        )}

        <div className="card" style={{ padding: 16, marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 280px" }}>
              <label style={{ display: "block", fontSize: 12, color: "var(--ink-600)", marginBottom: 4 }}>
                Matching kind
              </label>
              <input
                value={kindInput}
                onChange={(e) => setKindInput(e.target.value)}
                placeholder="event.pack.*"
                style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border)" }}
              />
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Comma-separated globs. <code>*</code> matches everything;{" "}
                <code>event.pack.*</code> matches all event-pack events.
              </div>
            </div>
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => void handleCreate()}>
              <Icon name="plus" size={13} /> Register webhook
            </button>
          </div>
        </div>

        <h2 style={{ fontSize: 15, margin: "0 0 8px", color: "var(--ink-700)" }}>Registered webhooks</h2>
        <div className="card" style={{ padding: 0, marginBottom: 24 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--ink-50)", textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-600)" }}>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Matching kind</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Status</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Signing key</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Receiver URL</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)", textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {regs === null && <tr><td colSpan={5} style={{ padding: 16 }}>Loading…</td></tr>}
              {regs && regs.length === 0 && (
                <tr><td colSpan={5} style={{ padding: 16, color: "var(--ink-500)" }}>No webhooks registered yet.</td></tr>
              )}
              {regs && regs.map((r) => (
                <tr key={r.id} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "10px 16px", fontFamily: "ui-monospace, monospace" }}>{r.matchingKind}</td>
                  <td style={{ padding: "10px 16px" }}>{r.status ?? "—"}</td>
                  <td style={{ padding: "10px 16px" }}>{r.hasSigningKey ? "on file" : "missing"}</td>
                  <td style={{ padding: "10px 16px", fontFamily: "ui-monospace, monospace", fontSize: 12, wordBreak: "break-all" }}>{r.url}</td>
                  <td style={{ padding: "10px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => void handleToggle(r)}>
                      {r.status === "active" ? "Deactivate" : "Activate"}
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 6 }} disabled={busy} onClick={() => void handleRemove(r.id)}>
                      <Icon name="trash" size={13} /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 12, marginBottom: 8 }}>
          <h2 style={{ fontSize: 15, margin: 0, color: "var(--ink-700)" }}>Received events</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => void refreshEvents()}>
            <Icon name="refresh" size={13} /> Refresh
          </button>
          <span className="muted" style={{ fontSize: 12 }}>Most recent first, capped at 100.</span>
        </div>
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--ink-50)", textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-600)" }}>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Kind</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Received</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Signature</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Payload</th>
              </tr>
            </thead>
            <tbody>
              {events === null && <tr><td colSpan={4} style={{ padding: 16 }}>Loading…</td></tr>}
              {events && events.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 16, color: "var(--ink-500)" }}>No events received yet. Trigger one in TidyHQ (activate a pack) — it should appear here within a few seconds.</td></tr>
              )}
              {events && events.map((e) => {
                const open = expanded[e.id] ?? false;
                return (
                  <React.Fragment key={e.id}>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "10px 16px", fontFamily: "ui-monospace, monospace" }}>{e.kind}</td>
                      <td style={{ padding: "10px 16px" }}>{fmt(e.receivedAt)}</td>
                      <td style={{ padding: "10px 16px" }}>
                        {e.signatureValid
                          ? <span style={{ color: "var(--green-600, #197f4c)" }}>verified ({e.signatureMatchedHeader})</span>
                          : <span style={{ color: "var(--amber-500, #b47d00)" }}>unverified</span>}
                      </td>
                      <td style={{ padding: "10px 16px" }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => setExpanded((s) => ({ ...s, [e.id]: !open }))}
                        >
                          {open ? "Hide" : "Show"}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={4} style={{ padding: "0 16px 16px" }}>
                          <JsonBlock value={e.payload} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: 12, margin: "24px 0 8px" }}>
          <h2 style={{ fontSize: 15, margin: 0, color: "var(--ink-700)" }}>Historical events (TidyHQ retention, last 30 days)</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => void fetchTriggered()}>
            <Icon name="download" size={13} /> Fetch all
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => void fetchTriggered(["event.pack.activated"])}>
            <Icon name="download" size={13} /> Fetch event.pack.activated
          </button>
        </div>
        <div className="card" style={{ padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: "var(--ink-50)", textAlign: "left", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--ink-600)" }}>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Kind</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Object</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Created</th>
                <th style={{ padding: "10px 16px", borderBottom: "1px solid var(--border)" }}>Payload</th>
              </tr>
            </thead>
            <tbody>
              {triggered === null && <tr><td colSpan={4} style={{ padding: 16, color: "var(--ink-500)" }}>Click a fetch button to pull events from TidyHQ.</td></tr>}
              {triggered && triggered.length === 0 && (
                <tr><td colSpan={4} style={{ padding: 16, color: "var(--ink-500)" }}>TidyHQ returned no events for that filter in the last 30 days.</td></tr>
              )}
              {triggered && triggered.map((e, i) => {
                const key = e.id ?? `${e.kind}-${i}`;
                const open = triggeredExpanded[key] ?? false;
                return (
                  <React.Fragment key={key}>
                    <tr style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={{ padding: "10px 16px", fontFamily: "ui-monospace, monospace" }}>{e.kind}</td>
                      <td style={{ padding: "10px 16px" }}>{e.object ?? "—"}</td>
                      <td style={{ padding: "10px 16px" }}>{fmt(e.created)}</td>
                      <td style={{ padding: "10px 16px" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setTriggeredExpanded((s) => ({ ...s, [key]: !open }))}>
                          {open ? "Hide" : "Show"}
                        </button>
                      </td>
                    </tr>
                    {open && (
                      <tr>
                        <td colSpan={4} style={{ padding: "0 16px 16px" }}>
                          <JsonBlock value={e} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
          Signed in to <b>{instance}</b>. Received event payloads are
          stored locally so we can inspect the schema of{" "}
          <code>event.pack.activated</code>. They aren't sent back to
          TidyHQ.
        </p>
      </main>
    </div>
  );
}
